import React from 'react';
import { Icon } from './Icon';
import type { ImportRow } from '../types/import';

interface ImportPreviewTableProps {
  data: ImportRow[];
  onRowSelect: (rowId: string, selected: boolean) => void;
  selectedRows: string[];
  onAssignToChange: (rowId: string, assignTo: string, assignToId: string) => void;
}

// Mock data para agentes - en un proyecto real vendría de la API
const mockAgents = [
  { id: '3', name: 'Agente 1' },
  { id: '4', name: 'Agente 2' },
  { id: '10', name: 'Esteban Valenzuela' },
];

export const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  data,
  onRowSelect,
  selectedRows,
  onAssignToChange
}) => {
  const handleSelectAll = (checked: boolean) => {
    data.forEach(row => {
      onRowSelect(row.id, checked);
    });
  };

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Vista Previa de Datos ({data.length} registros)
        </h3>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={allSelected}
            ref={input => {
              if (input) input.indeterminate = someSelected;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Seleccionar todos
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200 dark:border-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Seleccionar
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Nombres
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Apellidos
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Teléfono
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Nombre del Evento
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Programa de Interés
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Asignar a
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Estado
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row) => (
              <tr
                key={row.id}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${
                  row.errors && row.errors.length > 0
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : ''
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={(e) => onRowSelect(row.id, e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.nombres}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.apellidos}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.email}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.telefono}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.nombreEvento}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {row.programaInteres}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={row.asignadoAId || ''}
                    onChange={(e) => {
                      const selectedAgent = mockAgents.find(a => a.id === e.target.value);
                      onAssignToChange(row.id, selectedAgent?.name || '', e.target.value);
                    }}
                    className="w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Seleccionar agente</option>
                    {mockAgents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {row.errors && row.errors.length > 0 ? (
                    <div className="flex items-center text-red-600 dark:text-red-400">
                      <Icon name="alert-circle" className="w-4 h-4 mr-1" />
                      <span className="text-xs">Error</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <Icon name="check" className="w-4 h-4 mr-1" />
                      <span className="text-xs">Válido</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.some(row => row.errors && row.errors.length > 0) && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
            Errores encontrados:
          </h4>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {data
              .filter(row => row.errors && row.errors.length > 0)
              .map((row) => (
                <li key={row.id}>
                  <strong>Fila {row.id}:</strong> {row.errors?.join(', ')}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};
